/**
 * RTWO Indexer Engine — Search Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { RTWOIndexType } from '../domain-models';
import type { IIndexSearchService, SearchResult } from '../service-interfaces';
import type { StorageAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class IndexSearchService extends BaseIndexerService implements IIndexSearchService {
  readonly serviceId = INDEXER_SERVICE_IDS.SEARCH_SERVICE;
  readonly serviceName = 'Index Search Service';
  readonly description =
    'Full-text search across all index types. No fake search results returned.';
  readonly integrations = ['Core Engine', 'Index Storage Binding', 'Explorer Platform (future)'];
  readonly requiresRPCSource = false;
  readonly requiresIndexStorage = true;

  constructor(
    context: IndexerServiceContext,
    private readonly storageRegistry: StorageAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_index_storage';
  }

  async search(searchText: string): Promise<SearchResult> {
    if (!this.storageRegistry.isConfigured()) {
      return {
        success: false,
        results: [],
        totalCount: 0,
        error: 'Index Storage Not Configured — No search results available.',
        isPlaceholder: true,
        source: 'Indexer Engine — Index Storage Not Configured',
      };
    }

    await this.storageRegistry.getActiveAdapter().search(searchText);
    this.context.eventBus.emit(IndexerInternalEventTypes.SEARCH_EXECUTED, { searchText });

    return {
      success: false,
      results: [],
      totalCount: 0,
      error: 'Index Storage Not Configured — No search results available.',
      isPlaceholder: true,
      source: 'Indexer Engine — Index Storage Not Configured',
    };
  }

  async searchByType(indexType: RTWOIndexType, searchText: string): Promise<SearchResult> {
    if (!this.storageRegistry.isConfigured()) {
      return {
        success: false,
        results: [],
        totalCount: 0,
        error: 'Index Storage Not Configured — No search results available.',
        isPlaceholder: true,
        source: 'Indexer Engine — Index Storage Not Configured',
      };
    }

    await this.storageRegistry.getActiveAdapter().searchByType(indexType, searchText);
    this.context.eventBus.emit(IndexerInternalEventTypes.SEARCH_EXECUTED, {
      indexType,
      searchText,
    });

    return {
      success: false,
      results: [],
      totalCount: 0,
      error: 'Index Storage Not Configured — No search results available.',
      isPlaceholder: true,
      source: 'Indexer Engine — Index Storage Not Configured',
    };
  }

  isSearchAvailable(): false {
    return false;
  }
}
